import {
	extractRulesFromSchema,
	emitRule,
	canonicalize,
	djb2Hash,
} from '../xallof/serialization';

// The parsed rule id is `rule-<index>-<identityHash>`; the identity hash
// (content-addressed over the rule's `if` + negation) is the substring after
// the last dash. Matching on it ignores positional index and x-allOf/allOf
// namespace, so reordering or migrating an unchanged rule is not a diff.
function identityKey(rule) {
	const id = String(rule.id || '');
	const dash = id.lastIndexOf('-');
	return dash === -1 ? id : id.slice(dash + 1);
}

// Full-content fingerprint (if + then + else + negation), used to decide
// whether two rules sharing an identity have actually changed.
function contentKey(rule) {
	const emitted = emitRule(rule);
	if (emitted == null) return '';
	return djb2Hash(JSON.stringify(canonicalize(emitted)));
}

/**
 * Compare conditional rules between two schemas, matching by content
 * identity rather than positional id. Returns the same
 * { added, modified, removed } shape as diffXAllOfRules.
 */
export function diffRulesForCompare(leftSchema, rightSchema) {
	const baseRules = extractRulesFromSchema(leftSchema || {}).rules;
	const targetRules = extractRulesFromSchema(rightSchema || {}).rules;

	const baseByIdentity = new Map(baseRules.map(r => [identityKey(r), r]));
	const targetByIdentity = new Map(targetRules.map(r => [identityKey(r), r]));

	const added = targetRules.filter(r => !baseByIdentity.has(identityKey(r)));
	const removed = baseRules.filter(r => !targetByIdentity.has(identityKey(r)));
	const modified = targetRules
		.filter(r => baseByIdentity.has(identityKey(r)))
		.map(r => ({ baseRule: baseByIdentity.get(identityKey(r)), rule: r }))
		.filter(({ baseRule, rule }) => contentKey(baseRule) !== contentKey(rule));

	return { added, modified, removed };
}
